import { c as _c } from "react/compiler-runtime";
import type { BranchPredicateConfig, NodeConfigurationContextBranch } from '@onefootprint/request-types/dashboard';
import { Form, LinkButton } from '@onefootprint/ui';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import useSidebar from '@/components/playbook-builder/hooks/use-sidebar';
import type { BranchFormData, FormBranchCase, FormBranchComparison } from '../../../types';
import getBranch from '../../../utils/get-branch';
import { Branch } from '../components/branch';
import DefaultBranch from '../components/default-branch';
type FormContentProps = {
  config: NodeConfigurationContextBranch['config'] | undefined;
  canEdit: boolean;
};
const getBranchComparison = (currentConfig?: BranchPredicateConfig | null): FormBranchComparison => ({
  op: currentConfig?.supportedOperators[0] ?? 'equals',
  value: ''
});
const getNewBranchCase = (currentConfig?: BranchPredicateConfig | null): FormBranchCase => {
  const id = crypto.randomUUID();
  return {
    branchId: id,
    branch: {
      name: '',
      successor: ''
    },
    condition: getBranchComparison(currentConfig)
  };
};
export const FormContent = t0 => {
  const $ = _c(82);
  const {
    config,
    canEdit
  } = t0;
  const {
    t
  } = useTranslation("playbook-builder");
  const {
    control
  } = useFormContext();
  let t1;
  if ($[0] !== control) {
    t1 = {
      control,
      name: "cases"
    };
    $[0] = control;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    fields,
    append,
    update,
    remove
  } = useFieldArray(t1);
  const branchOn = useWatch({
    control,
    name: "branchPredicateId"
  }) ?? undefined;
  let t2;
  if ($[2] !== branchOn || $[3] !== config) {
    t2 = getBranch(branchOn, config);
    $[2] = branchOn;
    $[3] = config;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const currentConfig = t2;
  const {
    open: openSidebar
  } = useSidebar();
  const {
    isAnyComponentDirty
  } = useDirty();
  let t3;
  if ($[5] !== append || $[6] !== currentConfig) {
    t3 = () => {
      append(getNewBranchCase(currentConfig));
    };
    $[5] = append;
    $[6] = currentConfig;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const addBranch = t3;
  let t4;
  if ($[8] !== config) {
    t4 = config?.map(_temp) ?? [];
    $[8] = config;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const branchOptions = t4;
  let t5;
  if ($[10] !== config || $[11] !== fields || $[12] !== update) {
    t5 = value => {
      const currentConfig_0 = getBranch(value, config);
      fields.forEach((oldCase, index) => {
        update(index, {
          branchId: oldCase.branchId,
          branch: oldCase.branch,
          condition: getBranchComparison(currentConfig_0)
        });
      });
    };
    $[10] = config;
    $[11] = fields;
    $[12] = update;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const onPredicateChange = t5;
  let t6;
  if ($[14] !== remove) {
    t6 = index_0 => () => {
      remove(index_0);
    };
    $[14] = remove;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const handleRemove = t6;
  const sourceNodeId = currentConfig?.sourceNodeIds?.[0];
  let t7;
  if ($[16] !== openSidebar || $[17] !== sourceNodeId) {
    t7 = sourceNodeId ? () => openSidebar(sourceNodeId) : undefined;
    $[16] = openSidebar;
    $[17] = sourceNodeId;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const handleClickViewSourceNode = t7;
  let t8;
  if ($[19] !== t) {
    t8 = t(_temp2);
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t8) {
    t9 = <p className="text-label-2 whitespace-nowrap">{t8}</p>;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const t10 = !sourceNodeId || isAnyComponentDirty;
  let t11;
  if ($[23] !== t) {
    t11 = t(_temp3);
    $[23] = t;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== handleClickViewSourceNode || $[26] !== t10 || $[27] !== t11) {
    t12 = <LinkButton onClick={handleClickViewSourceNode} disabled={t10}>{t11}</LinkButton>;
    $[25] = handleClickViewSourceNode;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12 || $[30] !== t9) {
    t13 = <div className="flex flex-row justify-between">{t9}{t12}</div>;
    $[29] = t12;
    $[30] = t9;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t) {
    t14 = t(_temp4);
    $[32] = t;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t14) {
    t15 = <div className="text-body-2 text-tertiary">{t14}</div>;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t13 || $[37] !== t15) {
    t16 = <div className="flex flex-col">{t13}{t15}</div>;
    $[36] = t13;
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t) {
    t17 = t(_temp5);
    $[39] = t;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== branchOptions) {
    t18 = value_0 => branchOptions.find(b => b.value === value_0)?.label ?? "";
    $[41] = branchOptions;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  const t19 = branchOptions.length === 0;
  let t20;
  if ($[43] !== branchOptions || $[44] !== onPredicateChange || $[45] !== t17 || $[46] !== t18 || $[47] !== t19) {
    t20 = <Form.Select size="compact" name="branchPredicateId" onChange={onPredicateChange} placeholder={t17} customFindOptionLabel={t18} disabled={t19} options={branchOptions} />;
    $[43] = branchOptions;
    $[44] = onPredicateChange;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== t16 || $[50] !== t20) {
    t21 = <div className="flex flex-col gap-2">{t16}{t20}</div>;
    $[49] = t16;
    $[50] = t20;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== t) {
    t22 = t(_temp6);
    $[52] = t;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== t22) {
    t23 = <p className="text-label-2 whitespace-nowrap mb-2">{t22}</p>;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== addBranch || $[57] !== canEdit || $[58] !== config || $[59] !== fields || $[60] !== handleRemove || $[61] !== t) {
    let t25;
    if ($[63] !== addBranch || $[64] !== canEdit || $[65] !== config || $[66] !== fields.length || $[67] !== handleRemove || $[68] !== t) {
      t25 = (field, index_1) => <div key={field.branchId}><Branch canEdit={canEdit} field={field} index={index_1} config={config} handleDelete={handleRemove(index_1)} disableDelete={fields.length <= 1} /><div className="flex flex-row ml-3 gap-3 p-3 h-10 border-l border-tertiary justify-between"><div className="text-body-2 text-tertiary">{t(_temp7)}</div>{canEdit && index_1 === fields.length - 1 && <LinkButton type="button" onClick={addBranch}>{t(_temp8)}</LinkButton>}</div></div>;
      $[63] = addBranch;
      $[64] = canEdit;
      $[65] = config;
      $[66] = fields.length;
      $[67] = handleRemove;
      $[68] = t;
      $[69] = t25;
    } else {
      t25 = $[69];
    }
    t24 = fields.map(t25);
    $[56] = addBranch;
    $[57] = canEdit;
    $[58] = config;
    $[59] = fields;
    $[60] = handleRemove;
    $[61] = t;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  let t25;
  if ($[70] !== t24) {
    t25 = <div className="flex flex-col">{t24}</div>;
    $[70] = t24;
    $[71] = t25;
  } else {
    t25 = $[71];
  }
  let t26;
  if ($[72] !== canEdit || $[73] !== currentConfig) {
    t26 = <DefaultBranch canEdit={canEdit} predicate={currentConfig} />;
    $[72] = canEdit;
    $[73] = currentConfig;
    $[74] = t26;
  } else {
    t26 = $[74];
  }
  let t27;
  if ($[75] !== t23 || $[76] !== t25 || $[77] !== t26) {
    t27 = <div className="flex flex-col">{t23}{t25}{t26}</div>;
    $[75] = t23;
    $[76] = t25;
    $[77] = t26;
    $[78] = t27;
  } else {
    t27 = $[78];
  }
  let t28;
  if ($[79] !== t21 || $[80] !== t27) {
    t28 = <div className="flex flex-col gap-6">{t21}{t27}</div>;
    $[79] = t21;
    $[80] = t27;
    $[81] = t28;
  } else {
    t28 = $[81];
  }
  return t28;
};
const BranchOption = t0 => {
  const $ = _c(15);
  const {
    branch
  } = t0;
  const hasDifferentDisplayName = branch.displayName !== branch.identifier;
  let t1;
  if ($[0] !== branch.identifier) {
    t1 = <span className="text-snippet-2 font-medium">{branch.identifier}</span>;
    $[0] = branch.identifier;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== branch.dataType) {
    t2 = branch.dataType.join(" | ");
    $[2] = branch.dataType;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <span className="text-snippet-2 text-tertiary">{t2}</span>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <div className="flex flex-row gap-2">{t1}{t3}</div>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== branch.displayName || $[10] !== hasDifferentDisplayName) {
    t5 = hasDifferentDisplayName && <span className="text-body-2 text-tertiary">{branch.displayName}</span>;
    $[9] = branch.displayName;
    $[10] = hasDifferentDisplayName;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div className="flex flex-col">{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
function _temp(branch) {
  return {
    value: branch.identifier,
    label: branch.displayName,
    component: <BranchOption branch={branch} />
  };
}
function _temp2($) {
  return $.branchNode.branchOn;
}
function _temp3($_0) {
  return $_0.branchNode.viewSource;
}
function _temp4($_1) {
  return $_1.branchNode.branchOnDescription;
}
function _temp5($_2) {
  return $_2.branchNode.selectBranch;
}
function _temp6($_3) {
  return $_3.branchNode.branchingLogic;
}
function _temp7($_4) {
  return $_4.rulesNode.branch.otherwiseLabel;
}
function _temp8($_5) {
  return $_5.branchNode.addBranch;
}